import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Download, ExternalLink, FileText, FolderOpen, Sparkles } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { FollowBrand } from "@/components/FollowBrand";
import { ShareBrand } from "@/components/ShareBrand";
import { FavouriteButton } from "@/components/Favourites";
import { DocumentCard } from "@/components/DocumentCard";
import { Reveal } from "@/components/Reveal";
import { asArray, downloadUrl, formatDate } from "@/lib/api";
import { useFetch, usePageTitle } from "@/lib/hooks";
import { cn } from "@/lib/utils";

const EASE = [0.22, 1, 0.36, 1];

export default function BrandPage() {
  const { slug } = useParams();
  const { data, loading, error } = useFetch(`/brands/${slug}`, null);
  const [tab, setTab] = useState("All");
  const brand = data?.brand;
  const docs = asArray(data?.documents);
  usePageTitle(brand ? `${brand.name} Price Lists` : "Brand", brand ? `${brand.name} price lists on PRICEHUB: preview and download current PDF and Excel price lists with effective dates. Independent directory, not affiliated with ${brand.name}.` : undefined);
  const tabs = useMemo(() => ["All", ...new Set(docs.map((d) => d.category))], [docs]);
  const shown = tab === "All" ? docs : docs.filter((d) => d.category === tab);
  const latest = useMemo(() => [...docs].sort((a, b) => (b.updated_date || "").localeCompare(a.updated_date || ""))[0], [docs]);

  if (loading) return <div className="mx-auto max-w-7xl space-y-4 px-5 pt-40"><div className="ph2-shimmer h-56 rounded-[2rem]" /><div className="grid gap-4 md:grid-cols-3">{[0, 1, 2].map((i) => <div key={i} className="ph2-shimmer h-60 rounded-[1.5rem]" />)}</div></div>;
  if (error || !brand) {
    return (
      <div className="mx-auto max-w-3xl px-5 pb-32 pt-44 text-center" data-testid="brand-not-found">
        <h1 className="font-display text-5xl font-extrabold text-ph-ink">Brand not found</h1>
        <Link to="/brands" className="mt-6 inline-block font-semibold text-ph-accent hover:underline" data-testid="brand-not-found-back">Browse all brands</Link>
      </div>
    );
  }

  return (
    <>
      <section className="relative overflow-hidden pb-14 pt-32 md:pt-40">
        <div className="ph2-grid absolute inset-0 -z-10" />
        <div className="ph2-orb absolute -top-20 right-0 -z-10 h-80 w-80 rounded-full bg-ph-violet/20 blur-[100px]" />
        <div className="mx-auto max-w-7xl px-5 md:px-8">
          <Link to="/brands" data-testid="brand-back-link" className="inline-flex items-center gap-2 text-sm font-medium text-ph-muted hover:text-ph-ink"><ArrowLeft className="h-4 w-4" /> All brands</Link>
          <div className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
              <motion.div initial={{ opacity: 0, scale: 0.8, rotate: -6 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ duration: 0.8, ease: EASE }}>
                <BrandLogo brand={brand} className="h-24 w-24 rounded-[1.75rem] md:h-28 md:w-28" textClass="text-2xl" />
              </motion.div>
              <div>
                <h1 className="overflow-hidden font-display text-5xl font-extrabold leading-none tracking-[-0.04em] text-ph-ink md:text-7xl" data-testid="brand-name">
                  <motion.span className="block" initial={{ y: "105%" }} animate={{ y: 0 }} transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}>{brand.name}</motion.span>
                </h1>
                <div className="mt-4 flex flex-wrap gap-2">
                  {(brand.categories ?? []).map((c) => (
                    <Link key={c} to={`/brands?category=${encodeURIComponent(c)}`} className="rounded-full border border-ph-line bg-ph-surface px-3 py-1 text-xs font-medium text-ph-muted hover:border-ph-accent/40 hover:text-ph-accent">{c}</Link>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="rounded-2xl border border-ph-line bg-ph-surface px-5 py-3"><p className="font-mono-ph text-[11px] uppercase tracking-wider text-ph-muted">Price lists</p><p className="flex items-center gap-2 font-display text-3xl font-extrabold text-ph-ink" data-testid="brand-doc-count"><FileText className="h-5 w-5 text-ph-violet" />{docs.length}</p></div>
              <div className="rounded-2xl border border-ph-line bg-ph-surface px-5 py-3"><p className="font-mono-ph text-[11px] uppercase tracking-wider text-ph-muted">Downloads</p><p className="flex items-center gap-2 font-display text-3xl font-extrabold text-ph-ink"><Download className="h-5 w-5 text-ph-accent" />{brand.download_count ?? 0}</p></div>
            </div>
          </div>
          {brand.description && <p className="mt-8 max-w-3xl text-base leading-relaxed text-ph-muted md:text-lg" data-testid="brand-description">{brand.description}</p>}
          {brand.description && /^https?:\/\//.test(brand.profile_source || "") && (
            <p className="mt-2 text-xs text-ph-muted" data-testid="brand-profile-source">Summarised from the <a href={brand.profile_source} target="_blank" rel="noopener noreferrer" className="underline hover:text-ph-ink">official {brand.name} website</a></p>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-4">
            <FollowBrand brand={brand} followers={data?.followers ?? 0} />
            <FavouriteButton brand={brand} size="lg" />
            <ShareBrand brand={brand} />
            {brand.website && (
              <a href={brand.website} target="_blank" rel="noopener noreferrer" data-testid="brand-website-link" className="inline-flex items-center gap-1.5 text-sm font-semibold text-ph-accent hover:underline">Official website <ExternalLink className="h-3.5 w-3.5" /></a>
            )}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-32 md:px-8">
        {latest && (
          <Reveal className="mb-8">
            <div className="flex flex-col gap-4 rounded-[1.75rem] border border-ph-lime/40 bg-gradient-to-r from-ph-lime/10 via-ph-surface to-ph-surface p-5 sm:flex-row sm:items-center sm:justify-between md:p-6" data-testid="brand-latest-list">
              <div className="min-w-0">
                <p className="flex items-center gap-2 font-mono-ph text-[11px] font-bold uppercase tracking-[0.2em] text-ph-accent"><Sparkles className="h-3.5 w-3.5" /> Newest effective date</p>
                <p className="mt-1 truncate font-display text-xl font-bold text-ph-ink md:text-2xl">{latest.title}</p>
                <p className="text-sm text-ph-muted">Effective {formatDate(latest.updated_date)}{latest.version ? ` · Rev ${latest.version}` : ""}</p>
              </div>
              {(latest.pdf || latest.excel) && (
                <a href={downloadUrl(latest.id, latest.pdf ? "pdf" : "excel")} download data-testid="brand-latest-download"
                  className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full bg-ph-lime px-6 py-3 text-sm font-semibold text-ph-navy transition-[box-shadow,transform] hover:-translate-y-0.5 hover:shadow-[0_0_28px_rgba(182,255,92,0.45)]">
                  <Download className="h-4 w-4" /> Download latest {latest.pdf ? "PDF" : "Excel"}
                </a>
              )}
            </div>
          </Reveal>
        )}
        {docs.length > 0 && (
          <div className="ph-noscroll mb-8 flex gap-2 overflow-x-auto" data-lenis-prevent>
            {tabs.map((t) => (
              <button key={t} type="button" onClick={() => setTab(t)} data-testid={`brand-tab-${t.toLowerCase().replace(/\s+/g, "-")}`}
                className={cn("relative shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors", tab === t ? "border-ph-lime text-ph-navy" : "border-ph-line bg-ph-surface text-ph-muted hover:text-ph-ink")}>
                {tab === t && <motion.span layoutId="brand-tab-pill" className="absolute inset-0 rounded-full bg-ph-lime" transition={{ type: "spring", stiffness: 400, damping: 34 }} />}
                <span className="relative">{t}</span>
              </button>
            ))}
          </div>
        )}
        {shown.length > 0 ? (
          <motion.div key={tab} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: EASE }} className="grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="brand-documents">
            {shown.map((d, i) => <Reveal key={d.id} delay={(i % 3) * 0.07}><DocumentCard doc={d} showBrand={false} /></Reveal>)}
          </motion.div>
        ) : (
          <div className="flex flex-col items-center rounded-[1.75rem] border border-dashed border-ph-line bg-ph-surface/70 px-6 py-20 text-center" data-testid="brand-documents-empty">
            <FolderOpen className="h-10 w-10 text-ph-accent" />
            <p className="mt-4 font-display text-2xl font-bold text-ph-ink">No price lists published yet</p>
            <p className="mt-2 max-w-md text-sm text-ph-muted">{brand.name} price lists will appear here as soon as they are uploaded. Check back soon or browse other brands.</p>
            <Link to="/brands" className="mt-6 rounded-full bg-ph-lime px-5 py-2.5 text-sm font-semibold text-ph-navy" data-testid="brand-empty-browse">Browse other brands</Link>
          </div>
        )}
      </section>
    </>
  );
}
